import { ResourceModel } from '@bryntum/schedulerpro';
import { useCallback } from 'react';
import { Team } from 'api/models';
import {
  DeleteAllocationsProps,
  FetchableResourceType,
  ResourceType,
} from 'api/resource-management/types';
import {
  UserAssignedToProjectParams,
  UserRemovedFromProjectParams,
} from 'components/organisms/resource-management/scheduler-config/listeners';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { useRemoveTeamFromLocalState } from 'queries/placeholder';
import { useAddUsersToProject } from '../../add-to-project';
import { AddResourceProps, GroupBy, Range } from '../../types';
import { filterAllocationEvents } from '../../utils/events/events';
import {
  resourceIdForAssignProject,
  resourceIdForProject,
  resourceIdForProjectAsLeaf,
  resourceIdForTeamAsLeaf,
} from '../../utils/id';
import { useAggregationManager } from '../aggregation-manager';
import { useAllocationAndSummaryManager } from '../resource-summary-manager';
import { CommonManagerProps } from '../types';
import { useTotalAllocationInUIManager } from '../ui-total-allocation-manager';
import { useAddRemoveResource } from './add-remove-resource';

interface Result {
  removeAllocationsForResource: (params: DeleteAllocationsProps) => void;
  removeUserFromProjectInUI: (params: UserRemovedFromProjectParams) => void;
  assignUserToProject: (param: UserAssignedToProjectParams) => void;
  removeTeamFromUI: (
    projectId: number,
    team: Team,
    rangeLoadedInUI?: Range,
    fromTemplate?: boolean,
  ) => void;
  addResource: (props: AddResourceProps) => ResourceModel | undefined;
}
export const useResourceManager = (props: CommonManagerProps): Result => {
  const { schedulerRef, groupBy, projectLookup, roleLookup, userLookup } =
    props;

  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);
  const { updateTotalAllocation, setTotalAllocationForResource } =
    useTotalAllocationInUIManager(props);
  const { removeAggregationForResource, getAllocatedMinutesFromAggregation } =
    useAggregationManager();
  const { addResource, removeResource } = useAddRemoveResource({
    groupBy,
    schedulerRef,
    projectLookup,
    roleLookup,
  });
  const removeTeamFromLocalState = useRemoveTeamFromLocalState();
  const { inviteMembersToProject } = useAddUsersToProject();

  const removeAllocationsForResource = useCallback(
    async ({ projectId, resourceIdentifier }: DeleteAllocationsProps) => {
      schedulerRef.current.instance.suspendRefresh();

      const differenceInTotalMinutes =
        -1 *
        getAllocatedMinutesFromAggregation(projectId, resourceIdentifier)
          .totalAllocatedMinutes;

      const allEvents: AllocationEventModel[] =
        schedulerRef.current.instance.eventStore.allRecords;
      const currentResourceEvents = filterAllocationEvents(allEvents, {
        resourceIdentifier:
          resourceIdentifier.type === ResourceType.User
            ? {
                type: ResourceType.User,
                userId: resourceIdentifier.userId,
              }
            : resourceIdentifier,
      });

      const currentResourceEventsInProject = currentResourceEvents.filter(
        (event) =>
          event.resourceId ===
          (resourceIdentifier.type === ResourceType.User
            ? resourceIdForProjectAsLeaf({
                projectId,
                userId: resourceIdentifier.userId,
                roleId: resourceIdentifier.roleId,
                referenceTeamId: resourceIdentifier.referenceTeamId,
              })
            : resourceIdForTeamAsLeaf(projectId, resourceIdentifier.teamId)),
      );

      if (currentResourceEventsInProject.length > 0) {
        currentResourceEventsInProject.forEach((event) => {
          event.set('minutes', 0);
          schedulerRef.current.instance.eventStore.remove(event);
        });
      }

      // TODO: To check if this is required
      updateTotalAllocation(
        projectId,
        resourceIdentifier.type === ResourceType.Role
          ? {
              type: ResourceType.Team,
              teamId: resourceIdentifier.teamId,
            }
          : resourceIdentifier,
        differenceInTotalMinutes,
      );
      setTotalAllocationForResource(projectId, resourceIdentifier, 0);

      removeAggregationForResource(
        resourceIdentifier.type === ResourceType.Role
          ? {
              type: ResourceType.Team,
              teamId: resourceIdentifier.teamId,
            }
          : resourceIdentifier,
        projectId,
      );

      schedulerRef.current.instance.resumeRefresh(true);
    },
    [
      getAllocatedMinutesFromAggregation,
      removeAggregationForResource,
      schedulerRef,
      setTotalAllocationForResource,
      updateTotalAllocation,
    ],
  );

  const removeUserFromProjectInUI = useCallback(
    ({
      projectId,
      userResource,
      rangeLoadedInUI,
    }: UserRemovedFromProjectParams) => {
      const {
        user: { userId },
        role,
        referenceTeam,
      } = userResource;

      removeAllocationsForResource({
        projectId,
        resourceIdentifier: {
          type: ResourceType.User,
          userId,
          roleId: role?.roleId,
          referenceTeamId: referenceTeam?.teamId,
        },
      });

      removeResource(projectId, userResource);

      if (rangeLoadedInUI != null) {
        schedulerRef.current.instance.suspendRefresh();

        updateAllocationAndSummary(
          {
            [FetchableResourceType.UserList]: [userId],
          },
          rangeLoadedInUI,
        ).then(() => {
          removeAggregationForResource(
            {
              type: ResourceType.User,
              userId,
              ...(referenceTeam
                ? {
                    roleId: role?.roleId,
                    referenceTeamId: referenceTeam.teamId,
                  }
                : {}),
            },
            projectId,
          );

          if (groupBy === GroupBy.Projects) {
            const totalAllocation =
              getAllocatedMinutesFromAggregation(projectId);

            const resource =
              schedulerRef.current.instance.resourceStore.getById(
                resourceIdForProject(projectId),
              );

            if (resource) {
              resource.set(
                'totalAllocation',
                totalAllocation.totalAllocatedMinutes,
              );
            }
          }
        });

        schedulerRef.current.instance.resumeRefresh(true);
      }
    },
    [
      getAllocatedMinutesFromAggregation,
      groupBy,
      removeAggregationForResource,
      removeAllocationsForResource,
      removeResource,
      schedulerRef,
      updateAllocationAndSummary,
    ],
  );

  const assignUserToProject = useCallback(
    async ({ userId, projectId }: UserAssignedToProjectParams) => {
      if (groupBy === GroupBy.Users) {
        const newlyAdded = addResource({
          projectId,
          resource: {
            type: ResourceType.User,
            user: userLookup[userId],
          },
        });

        inviteMembersToProject(projectId, [userLookup[userId]]).catch(() => {
          schedulerRef.current.instance.suspendRefresh();
          schedulerRef.current.instance.resourceStore.remove(newlyAdded);
          const assignProject =
            schedulerRef.current.instance.resourceStore.getById(
              resourceIdForAssignProject(ResourceType.User, userId),
            );

          assignProject.set('alreadyAssignedProjectIds', [
            ...assignProject.get('alreadyAssignedProjectIds'),
            projectId,
          ]);
          assignProject.set(
            'alreadyAssignedProjectIds',
            assignProject
              .get('alreadyAssignedProjectIds')
              .filter((id) => id !== projectId),
          );
          schedulerRef.current.instance.resumeRefresh(true);
        });
      }
    },
    [addResource, groupBy, inviteMembersToProject, schedulerRef, userLookup],
  );

  const removeTeamFromUI = useCallback(
    /** team param here will only have teamId and identityType when coming from socket handler */
    (
      projectId: number,
      team: Team,
      rangeLoadedInUI?: Range,
      fromTemplate = false,
    ): void => {
      const { teamId } = team;
      const resource = schedulerRef.current.instance.resourceStore.getById(
        resourceIdForTeamAsLeaf(projectId, teamId),
      );

      if (resource == null) {
        return;
      }

      const roleId: number = resource.parent.get('roleId');

      removeAllocationsForResource({
        projectId,
        resourceIdentifier: {
          type: ResourceType.Role,
          roleId,
          teamId,
        },
      });

      removeResource(projectId, {
        type: ResourceType.Team,
        team,
      });
      if (!fromTemplate) {
        removeTeamFromLocalState(projectId, teamId);
      }

      if (rangeLoadedInUI != null) {
        schedulerRef.current.instance.suspendRefresh();

        updateAllocationAndSummary(
          {
            [FetchableResourceType.RoleList]: [roleId],
          },
          rangeLoadedInUI,
        ).then(() => {
          removeAggregationForResource(
            {
              type: ResourceType.Team,
              teamId: team.teamId,
            },
            projectId,
          );

          if (groupBy === GroupBy.Projects) {
            const totalAllocation =
              getAllocatedMinutesFromAggregation(projectId);

            const projectResource =
              schedulerRef.current.instance.resourceStore.getById(
                resourceIdForProject(projectId),
              );

            projectResource.set(
              'totalAllocation',
              totalAllocation.totalAllocatedMinutes,
            );
          }
        });

        schedulerRef.current.instance.resumeRefresh(true);
      }
    },
    [
      getAllocatedMinutesFromAggregation,
      groupBy,
      removeAggregationForResource,
      removeAllocationsForResource,
      removeResource,
      removeTeamFromLocalState,
      schedulerRef,
      updateAllocationAndSummary,
    ],
  );

  return {
    removeAllocationsForResource,
    removeUserFromProjectInUI,
    assignUserToProject,
    removeTeamFromUI,
    addResource,
  };
};
